import '../assets/NcButton-DhaPcomf.css';
import { n as f } from "../chunks/_plugin-vue2_normalizer-Bj5bLKV4.mjs";
const y = {
  name: "NcButton",
  inject: {
    getNcPopoverTriggerAttrs: {
      from: "NcPopover:trigger:attrs",
      default: () => () => ({})
    }
  },
  props: {
    /**
     * Set the text and icon alignment
     *
     * @default 'center'
     */
    alignment: {
      type: String,
      default: "center",
      validator: (t) => ["start", "start-reverse", "center", "center-reverse", "end", "end-reverse"].includes(t)
    },
    /**
     * Toggles the disabled state of the button on and off.
     */
    disabled: {
      type: Boolean,
      default: !1
    },
    /**
     * Specifies the button type
     * Accepted values: primary, secondary, tertiary, tertiary-no-background, tertiary-on-primary, error, warning, success. If left empty,
     * the default button style will be applied.
     */
    type: {
      type: String,
      validator(t) {
        return ["primary", "secondary", "tertiary", "tertiary-no-background", "tertiary-on-primary", "error", "warning", "success"].indexOf(t) !== -1;
      },
      default: "secondary"
    },
    /**
     * Specifies the button native type
     * Accepted values: submit, reset, button. If left empty,
     * the default "button" type will be used.
     */
    nativeType: {
      type: String,
      validator(t) {
        return ["submit", "reset", "button"].indexOf(t) !== -1;
      },
      default: "button"
    },
    /**
     * Specifies whether the button should span all the available width.
     * By default, buttons span the whole width of the container.
     */
    wide: {
      type: Boolean,
      default: !1
    },
    /**
     * Always try to provide an aria-label to your button. Make it more
     * specific than the button's name by provide some more context. E.g. if
     * the name of the button is "send" in the Mail app, the aria label could
     * be "Send email".
     */
    ariaLabel: {
      type: String,
      default: null
    },
    /**
     * Providing the href attribute turns the button component into an `a`
     * element.
     */
    href: {
      type: String,
      default: null
    },
    /**
     * Providing the download attribute with href downloads file when clicking.
     */
    download: {
      type: String,
      default: null
    },
    /**
     * Providing the to attribute turns the button component into a `router-link`
     * element. Takes precedence over the href attribute.
     */
    to: {
      type: [String, Object],
      default: null
    },
    /**
     * Pass in `true` if you want the matching behaviour of `router-link` to
     * be non-inclusive: https://router.vuejs.org/api/#exact
     */
    exact: {
      type: Boolean,
      default: !1
    },
    /**
     * @deprecated To be removed in @nextcloud/vue 9. Migration guide: remove ariaHidden prop from NcAction* components.
     * @todo Add a check in @nextcloud/vue 9 that this prop is not provided,
     * otherwise root element will inherit incorrect aria-hidden.
     */
    ariaHidden: {
      type: Boolean,
      default: null
    },
    /**
     * The pressed state of the button if it has a checked state
     * This will add the `aria-pressed` attribute and for the button to have the primary style in checked state.
     *
     * Pressed state is not supported for links
     */
    pressed: {
      type: Boolean,
      default: null
    }
  },
  emits: ["update:pressed", "click"],
  computed: {
    /**
     * The real type to be used for the button, enforces `primary` for pressed state and, if stateful button, any other type for not pressed state
     * Otherwise the type property is used.
     */
    realType() {
      return this.pressed ? "primary" : this.pressed === !1 && this.type === "primary" ? "secondary" : this.type;
    },
    /**
     * The flexbox alignment of the button content
     */
    flexAlignment() {
      return this.alignment.split("-")[0];
    },
    /**
     * If the button content should be reversed (icon on the end)
     */
    isReverseAligned() {
      return this.alignment.includes("-");
    },
    ncPopoverTriggerAttrs() {
      return this.getNcPopoverTriggerAttrs();
    }
  },
  /**
   * The render function to display the component
   *
   * @param {Function} h The function to create VNodes
   * @return {object|undefined} The created VNode
   */
  render(t) {
    var o, a, u;
    const r = !!this.$slots.default, s = (o = this.$slots) == null ? void 0 : o.icon;
    !r && !this.ariaLabel && console.warn(
      "You need to fill either the text or the ariaLabel props in the button component.",
      {
        text: (u = (a = this.$slots.default) == null ? void 0 : a[0]) == null ? void 0 : u.text,
        ariaLabel: this.ariaLabel
      },
      this
    );
    const e = this.to || this.href, i = !e && typeof this.pressed == "boolean", l = ({ href: p, navigate: n, isActive: c, isExactActive: h } = {}) => t(
      e ? "a" : "button",
      {
        class: [
          "button-vue",
          {
            "button-vue--icon-only": s && !r,
            "button-vue--text-only": r && !s,
            "button-vue--icon-and-text": s && r,
            ["button-vue--vue-".concat(this.realType)]: this.realType,
            "button-vue--wide": this.wide,
            ["button-vue--".concat(this.flexAlignment)]: this.flexAlignment !== "center",
            "button-vue--reverse": this.isReverseAligned,
            active: c,
            "router-link-exact-active": h
          }
        ],
        attrs: {
          "aria-label": this.ariaLabel,
          "aria-pressed": i ? this.pressed.toString() : void 0,
          disabled: this.disabled,
          type: e ? null : this.nativeType,
          role: e ? "button" : null,
          href: this.to ? p : this.href || null,
          target: e ? "_self" : null,
          rel: e ? "nofollow noreferrer noopener" : null,
          download: !this.to && this.href && this.download ? this.download : null,
          // If this button is used as a popover trigger, we need to apply trigger attrs, e.g. aria attributes
          ...this.ncPopoverTriggerAttrs,
          // Inherit all the component attrs
          ...this.$attrs
        },
        on: {
          ...this.$listeners,
          click: (d) => {
            i && this.$emit("update:pressed", !this.pressed), this.$emit("click", d), n == null || n(d);
          }
        }
      },
      [
        t("span", { class: "button-vue__wrapper" }, [
          s ? t(
            "span",
            {
              class: "button-vue__icon",
              attrs: {
                "aria-hidden": "true"
              }
            },
            [this.$slots.icon]
          ) : null,
          r ? t("span", { class: "button-vue__text" }, [this.$slots.default]) : null
        ])
      ]
    );
    return this.to ? t("router-link", {
      props: {
        custom: !0,
        to: this.to,
        exact: this.exact
      },
      scopedSlots: {
        default: l
      }
    }) : l();
  }
}, b = null, g = null;
var v = /* @__PURE__ */ f(
  y,
  b,
  g,
  !1,
  null,
  "fe3b5af5",
  null,
  null
);
const x = v.exports;
export {
  x as default
};
